'use client'

import type { ClipboardEvent } from 'react'

import { useCallback } from 'react'

/**
 * One piece of a composite label — a symbol, a value, a badge — painted as its own inline/flex
 * node.
 */
export interface CopySegment {
  /**
   * The segment's on-screen text. Matched as a whole token against the selection, so a segment
   * counts as selected only when its text appears delimited — never as a substring of a neighbour.
   * Falsy segments are skipped (e.g. an idle-liquidity market has no collateral).
   */
  text: string | undefined | null
  /**
   * Prepended before this segment's text when it follows an earlier selected segment — the
   * canonical join the on-screen layout omits (`' | '` for a divider drawn as a rule, `' '` before
   * an LLTV). Ignored when this is the first selected segment.
   */
  separator?: string
}

/**
 * Returns an `onCopy` handler that rewrites the clipboard with a clean, single-line copy of a
 * composite label whose pieces render as separate inline/flex nodes.
 *
 * A raw selection across such nodes serializes to a garbled, multi-line value: each blockified flex
 * item is newline-separated, and the canonical separators — a divider drawn as a rule, the space
 * before an LLTV — are not part of the copied text (`USDC\ncbBTC`). This rebuilds the copy from
 * exactly the segments the user highlighted, in order, joined by their separators: the whole label
 * copies as `USDC | cbBTC 86%`, a loan+collateral drag as `USDC | cbBTC`, a single symbol as
 * itself.
 *
 * Segments match as whole tokens, so a loan symbol contained in the collateral (DAI in sDAI, USDC
 * in syrupUSDC) never lets a collateral-only highlight smuggle in the loan. It rewrites the
 * clipboard only when the *entire* selection is recognised segments; a drag that runs past the
 * label into a neighbour (an adjacent table cell, a portaled warning tooltip) or that starts
 * mid-symbol carries unrecognised tokens and is left to the native copy — better an untouched copy
 * than one silently missing the parts it couldn't place. Mark decorative or screen-reader-only text
 * `select-none` so it stays out of the selection.
 */
export function useSegmentedCopy(segments: CopySegment[]) {
  return useCallback(
    (event: ClipboardEvent<HTMLElement>) => {
      const selection = window.getSelection()
      if (!selection || selection.isCollapsed) return

      // Each piece renders as its own blockified flex item, so the selection serializes them
      // whitespace-separated — split back into whole tokens (substring matching would count a loan
      // symbol contained in the collateral, e.g. DAI in sDAI).
      const selectedTokens = selection.toString().split(/\s+/).filter(Boolean)
      const segmentTexts = new Set(
        segments.map(segment => segment.text).filter((value): value is string => Boolean(value))
      )

      // Bail unless every selected token is a known segment: a selection that escaped into a
      // neighbour or a portaled tooltip carries tokens we can't place, and rewriting would drop
      // them from the copy. Leaving it lets the native copy keep everything the user highlighted.
      if (selectedTokens.length === 0 || selectedTokens.some(token => !segmentTexts.has(token))) {
        return
      }

      const selected = new Set(selectedTokens)
      let text = ''
      for (const segment of segments) {
        if (!segment.text || !selected.has(segment.text)) continue
        text = text ? text + (segment.separator ?? '') + segment.text : segment.text
      }
      if (!text) return

      event.clipboardData.setData('text/plain', text)
      event.preventDefault()
    },
    [segments]
  )
}
